import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../../query';
import { ExportQueryFn } from '../../../query/hooks';
import { RequestMeta } from '../../../temporary/requestmeta.types';
import { useBackerApi } from '../../api';
import { ListsSearch } from '../../lists/search.types';
import { backerKeys } from '../../query-key-factory';
import { ConsumerListResponse } from '../lists/lists.types';
import {
  ConsumerDirectoryBacker,
  ConsumerDirectoryResponse,
  DirectoryBackerSearch,
} from './directory.types';

const useGetConsumerDirectory = (causeId?: string) => {
  const BackerApi = useBackerApi();
  return async (context: {
    meta?: { queryString?: string };
  }): Promise<ConsumerDirectoryResponse> => {
    const { queryString } = context.meta || {};
    const { data } = await BackerApi.get<ConsumerDirectoryResponse>(
      `/consumer/directory/causes/${causeId}/backers?${queryString}`
    );
    return data;
  };
};

const useListConsumerDirectory = ({
  causeId,
  queryOptions,
  pagination,
}: {
  causeId?: string;
  queryOptions?: DirectoryBackerSearch;
  pagination?: QueryPagination;
}) => {
  const getConsumerDirectory = useGetConsumerDirectory(causeId);
  const { queryString, queryObject, queryPagination } = createQueryOptions<DirectoryBackerSearch>({
    search: queryOptions,
    pagination,
  });

  const key = causeId
    ? [...backerKeys.consumer.causes.id(causeId).directory.all, queryObject, queryPagination]
    : [];

  return useQuery({
    queryKey: key,
    queryFn: getConsumerDirectory,
    enabled: !!causeId,
    meta: { queryString, causeId },
  });
};

const useExportConsumerDirectory: ExportQueryFn<{ data: ConsumerDirectoryBacker[] }> = ({
  isExporting,
  queryOptions,
  causeId,
}: {
  isExporting: boolean;
  queryOptions?: DirectoryBackerSearch;
  causeId?: string;
}) => {
  const getConsumerDirectory = useGetConsumerDirectory(causeId);

  const exportData = async ({ pageParam }: { pageParam: { limit: number; offset: number } }) => {
    const { queryString } = createQueryOptions<DirectoryBackerSearch>({
      search: { ...queryOptions, options: { include: 'lists,properties' } },
      pagination: {
        limit: pageParam.limit,
        offset: pageParam.offset,
      },
    });
    const result = await getConsumerDirectory({ meta: { queryString } });
    return { data: result.backers, ...result };
  };

  const key = causeId ? backerKeys.consumer.causes.id(causeId).directory.export : [];

  return useInfiniteQuery({
    queryKey: key,
    queryFn: exportData,
    enabled: !!causeId && !!isExporting,
    initialPageParam: { limit: 250, offset: 0 },
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      const { meta } = lastPage as { meta: RequestMeta };
      if (!meta) return undefined;
      const { has_more, limit, offset } = meta;
      if (!has_more) {
        return undefined;
      }
      return { limit, offset: offset + limit };
    },
  });
};

const useShowConsumerDirectoryBacker = ({
  causeId,
  backerId,
}: {
  causeId?: string;
  backerId?: string;
}) => {
  const BackerApi = useBackerApi();
  const get = async () => {
    const { data } = await BackerApi.get<{ backer: ConsumerDirectoryBacker }>(
      `/consumer/directory/causes/${causeId}/backers/${backerId}`
    );
    return data.backer;
  };

  const key =
    causeId && backerId
      ? [...backerKeys.consumer.causes.id(causeId).directory.id(backerId).all]
      : [];

  return useQuery({
    queryKey: key,
    queryFn: get,
    enabled: !!causeId && !!backerId,
  });
};

const useGetConsumerDirectoryLists = () => {
  const BackerApi = useBackerApi();

  const getConsumerDirectoryLists = async (context: {
    meta?: { queryString?: string; causeId?: string };
  }): Promise<ConsumerListResponse> => {
    const { queryString, causeId } = context.meta || {};
    const { data } = await BackerApi.get<ConsumerListResponse>(
      `/consumer/directory/causes/${causeId}/lists?${queryString}`
    );
    return data;
  };

  return getConsumerDirectoryLists;
};

const useListConsumerDirectoryLists = ({
  causeId,
  queryOptions,
  pagination,
}: {
  causeId?: string;
  queryOptions?: ListsSearch;
  pagination?: QueryPagination;
}) => {
  const getConsumerDirectoryLists = useGetConsumerDirectoryLists();

  const { queryString, queryObject, queryPagination } = createQueryOptions<ListsSearch>({
    search: queryOptions,
    pagination,
  });

  const key = causeId ? backerKeys.consumer.causes.id(causeId).directory.lists : [];

  return useQuery({
    queryKey: [...key, queryObject, queryPagination],
    queryFn: getConsumerDirectoryLists,
    enabled: !!causeId,
    meta: { queryString, causeId },
  });
};

export {
  useExportConsumerDirectory,
  useListConsumerDirectory,
  useListConsumerDirectoryLists,
  useShowConsumerDirectoryBacker,
};
